<template>
  <section class="quote-section shipping-information">
    <h3>Shipping</h3>
    <div class="inputs">
      <TextBox v-model="quote.DATEREQUIRED" label="Date Required" type="date" class="input-item" :required="true" :invalid="showValidationErrors && !quote.DATEREQUIRED" />
      <Dropdown v-model="quote.SHIPVIA" :options="shipViaOptions" label="Ship Via" :canBeEmpty="true" class="input-item" />
      <TextBox label="Shipping Phone" format="phone" v-model="quote.SHIPPHONE" class="input-item" :required="true" :invalid="showValidationErrors && !quote.SHIPPHONE" />
      <TextBox
        label="Shipping Email"
        v-model="quote.SHIPEMAIL"
        class="input-item"
        :showExtLink="quote.SHIPEMAIL?.includes('@')"
        @navigateToLink="navigateToEmail" />

      <div class="address-row">
        <AddressSearch
          label="Address Line 1"
          class="address-line"
          v-model="quote.SHIPLINE1"
          :options="lookupResults"
          :showExtLink="hasShippingAddress"
          @navigateToLink="navigateToAddress"
          @autofillAddress="autofillAddress"
          @clearLookup="lookupResults.length = 0" />
        <TextBox label="Line 2" v-model="quote.SHIPLINE2" class="address-line" />
        <AddressSearch label="City" v-model="quote.SHIPCITY" :options="lookupResults" class="city" @autofillAddress="autofillAddress" @clearLookup="lookupResults.length = 0" />
        <div class="state-zip">
          <Dropdown label="State" v-model="quote.SHIPSTATE" :options="stateOptions" :canBeEmpty="true" class="state" />
          <AddressSearch label="Zip" v-model="quote.SHIPPOSTALCODE" :options="lookupResults" class="zip" @autofillAddress="autofillAddress" @clearLookup="lookupResults.length = 0" />
        </div>
      </div>

      <TextArea v-model="quote.NOTES" label="Notes" class="input-item full-width" :rows="3" />
    </div>
  </section>
</template>

<script>
import TextBox from '@/components/inputs/TextBox.vue';
import Dropdown from '@/components/inputs/Dropdown.vue';
import TextArea from '@/components/inputs/TextArea.vue';
import AddressSearch from '@/components/inputs/AddressSearch.vue';
import useAddressLookup from '@/composables/useAddressLookup.js';
import useStatesAndCountries from '@/composables/useStatesAndCountries.js';

export default {
  name: 'QuoteShippingInformation',
  components: {
    TextBox,
    Dropdown,
    TextArea,
    AddressSearch,
  },
  props: {
    quote: {
      type: Object,
      required: true,
    },
    showValidationErrors: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { stateAbbreviations } = useStatesAndCountries();
    const { addressLookup, lookupResults } = useAddressLookup();

    return {
      stateAbbreviations,
      addressLookup,
      lookupResults,
    };
  },
  data() {
    return {
      shipViaOptions: [
        { text: 'Will Call', value: 'Will Call' },
        { text: 'Western Interlock', value: 'Western Interlock' },
        { text: 'Material Transport', value: 'Material Transport' },
        { text: 'Open Road', value: 'Open Road' },
        { text: 'Bedrock Logistics', value: 'Bedrock Logistics' },
        { text: 'Freres', value: 'Freres' },
      ],
    };
  },
  computed: {
    stateOptions() {
      return this.stateAbbreviations;
    },
    hasShippingAddress() {
      return !!(this.quote?.SHIPLINE1 || this.quote?.SHIPCITY || this.quote?.SHIPSTATE || this.quote?.SHIPPOSTALCODE);
    },
  },
  watch: {
    'quote.SHIPLINE1'() {
      this.updateAddressLookup();
    },
    'quote.SHIPCITY'() {
      this.updateAddressLookup();
    },
    'quote.SHIPSTATE'() {
      this.updateAddressLookup();
    },
    'quote.SHIPPOSTALCODE'() {
      this.updateAddressLookup();
    },
  },
  methods: {
    autofillAddress(option) {
      if (option.address) this.quote.SHIPLINE1 = option.address;
      if (option.city) this.quote.SHIPCITY = option.city;
      if (option.state) this.quote.SHIPSTATE = option.state;
      if (option.zip) this.quote.SHIPPOSTALCODE = option.zip;
    },
    updateAddressLookup() {
      if (this.quote?.SHIPLINE1 && this.quote?.SHIPCITY && this.quote?.SHIPSTATE && this.quote?.SHIPPOSTALCODE) return;
      this.addressLookup(this.quote?.SHIPLINE1, this.quote?.SHIPCITY, this.quote?.SHIPSTATE, this.quote?.SHIPPOSTALCODE);
    },
    navigateToAddress() {
      window.open(
        `https://www.google.com/maps/search/${encodeURIComponent(this.quote.SHIPLINE1 || '')} ${encodeURIComponent(this.quote.SHIPLINE2 || '')} ${encodeURIComponent(this.quote.SHIPCITY || '')} ${encodeURIComponent(
          this.quote.SHIPSTATE || ''
        )} ${encodeURIComponent(this.quote.SHIPPOSTALCODE || '')}`,
        '_blank'
      );
    },
    navigateToEmail() {
      window.open(`mailto:${this.quote.SHIPEMAIL}`, '_blank');
    },
  },
};
</script>

<style lang="scss" scoped>
.quote-section {
  h3 {
    margin: 0 0 1.5rem;
    color: $textColor87;
  }

  .inputs {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 1.5rem 1rem;

    .input-item {
      min-width: 0;
      width: 100%;

      &.full-width {
        grid-column: 1 / -1;
      }
    }

    .address-row {
      grid-column: 1 / -1;
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 1.5rem 1rem;
      align-items: flex-start;

      .address-line,
      .city,
      .state-zip,
      .state,
      .zip {
        min-width: 0;
        width: 100%;
      }

      .state-zip {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 1rem;
      }
    }
  }

  @media (max-width: 75rem) {
    .inputs {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .address-row {
      grid-template-columns: 1fr 1fr;
    }
  }

  @media (max-width: 40rem) {
    .inputs {
      grid-template-columns: minmax(0, 1fr);

      .input-item.full-width {
        grid-column: 1 / -1;
      }

      .address-row {
        grid-template-columns: minmax(0, 1fr);
      }
    }
  }
}
</style>
